/**
 * Card #389 (review of #525): a pay-as-you-go contact / lead CSV import that would add
 * PAYG_CONFIRM_THRESHOLD or more new contacts asks "You'll be charged up to $X" first and is sent
 * only after OK. merch-backend has the same threshold (payAsGoCounter.mjs PAYG_CONFIRM_THRESHOLD)
 * and refuses an unconfirmed import of that size, or one where it counts MORE new contacts than
 * were confirmed, before charging anything.
 *
 * The screen only knows how many rows the file has, so the number it confirms is an upper bound:
 * rows that turn out invalid or already exist are not created and not charged. That is why the
 * message says "up to".
 */

/** Same value as merch-backend PAYG_CONFIRM_THRESHOLD. */
export const PAYG_CONFIRM_THRESHOLD = 2000;
/** $1 per new contact on pay-as-you-go (merch-backend PAYG_CONTACT_PRICE_CENTS = 100). */
export const PAYG_CONTACT_PRICE_USD = 1;
/** Upload modes that add contacts (update_replace only edits existing ones). */
export const CONTACT_ADDING_STATUSES: readonly string[] = ['create', 'update'];

export interface ImportChargeConfirmInput {
  /** Contact or lead upload (not items / images). */
  addsContacts: boolean;
  /** The upload mode chosen on the form. */
  status: unknown;
  /** validAdd from the header endpoint: pay-as-you-go plans answer { boolValue: true, limit: 0 }. */
  remaining: unknown;
  /** Data rows in the file (raw count) and rows left after blank rows are dropped. */
  rawRows: unknown;
  keptRows: unknown;
}

export interface ImportChargeConfirmation {
  /** New contacts the user is asked to agree to pay for (sent as confirmed_new_contacts). */
  count: number;
  /** Most it can cost, in dollars. */
  amountUsd: number;
}

/** The upload form field the server validates (merch-backend uploadFile.dto.mjs). */
export const CONFIRMED_NEW_CONTACTS_FIELD = 'confirmed_new_contacts';

/**
 * Did the upload request fail because the server refused confirmed_new_contacts (a 400 whose
 * validation errors name that field)? Lets the screen show a dedicated message instead of a generic one.
 */
export function isUploadChargeRefusal(error: unknown): boolean {
  const errors = (error as { error?: { errors?: unknown } })?.error?.errors;
  return Array.isArray(errors) && errors.some((e) => (e as { field?: unknown })?.field === CONFIRMED_NEW_CONTACTS_FIELD);
}

const wholeCount = (v: unknown): number => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0);

/** limit 0 (number or string) is how the header endpoint marks a pay-as-you-go plan. */
export function isPayAsYouGoAllowance(remaining: unknown): boolean {
  if (!remaining || typeof remaining !== 'object') return false;
  const limit = (remaining as { limit?: unknown }).limit;
  return limit === 0 || limit === '0';
}

/**
 * The confirmation this upload needs, or null when none is needed (not pay-as-you-go, does not add
 * contacts, or under the threshold). The count is the larger of the two row counts so it is never
 * below what the server can count from the same file.
 */
export function importChargeConfirmation(input: ImportChargeConfirmInput): ImportChargeConfirmation | null {
  if (!input.addsContacts) return null;
  if (typeof input.status !== 'string' || !CONTACT_ADDING_STATUSES.includes(input.status)) return null;
  if (!isPayAsYouGoAllowance(input.remaining)) return null;
  const count = Math.max(wholeCount(input.rawRows), wholeCount(input.keptRows));
  if (count < PAYG_CONFIRM_THRESHOLD) return null;
  return { count, amountUsd: count * PAYG_CONTACT_PRICE_USD };
}

export interface FreeFirstImportInput {
  /** Contact or lead upload (not items / images). */
  addsContacts: boolean;
  /** The upload mode chosen on the form. */
  status: unknown;
  /** validAdd from the header endpoint. */
  remaining: unknown;
  /** free_import_available from the header endpoint (card #624). */
  freeImportAvailable: unknown;
}

/**
 * Card #625: a pay-as-you-go contact/lead upload that adds contacts, while this workspace's free
 * first import is still unused, shows the free-import message INSTEAD of importChargeConfirmation's
 * charge question - whatever the file size, since PAYG_CONFIRM_THRESHOLD is about what gets charged
 * and nothing is charged here either way. A flag that is missing or not exactly `true` (the read
 * failed, or it was never asked) answers false, so on doubt the normal charge confirmation applies.
 */
export function isFreeFirstImport(input: FreeFirstImportInput): boolean {
  if (!input.addsContacts) return false;
  if (typeof input.status !== 'string' || !CONTACT_ADDING_STATUSES.includes(input.status)) return false;
  if (!isPayAsYouGoAllowance(input.remaining)) return false;
  return input.freeImportAvailable === true;
}
